import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { studioAIDivision } from '@/lib/api';

type Step = 'division_name' | 'tagline' | 'focus' | 'market' | 'submitting' | 'done';

const FOCUS_OPTIONS = [
  { id: 'AI Strategy & Advisory', label: 'AI Strategy & Advisory', description: 'Help clients build a roadmap for AI adoption' },
  { id: 'AI App Development', label: 'AI App Development', description: 'Build custom AI-powered apps and tools' },
  { id: 'AI Automation', label: 'AI Automation', description: 'Automate workflows and processes with AI' },
  { id: 'AI Training & Workshops', label: 'AI Training & Workshops', description: 'Train teams to work alongside AI tools' },
  { id: 'Full-Service AI', label: 'Full-Service AI', description: 'Strategy, implementation, and training — all of it' },
];

const MARKET_OPTIONS = [
  'Small businesses (1–50 employees)',
  'Professional services (legal, accounting, HR)',
  'Healthcare practices',
  'Retail & restaurants',
  'Manufacturing & trades',
  'Any SMB that wants AI help',
];

export function AIDivisionSetup() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('division_name');
  const [divisionName, setDivisionName] = useState('');
  const [tagline, setTagline] = useState('');
  const [focusAreas, setFocusAreas] = useState<string[]>([]);
  const [targetMarket, setTargetMarket] = useState('');
  const [brandUrl, setBrandUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  function toggleFocus(id: string) {
    setFocusAreas(prev =>
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id],
    );
  }

  async function handleSubmit() {
    if (!divisionName.trim() || focusAreas.length === 0 || !targetMarket) return;
    setStep('submitting');
    setError(null);
    try {
      const result = await studioAIDivision.setup({
        division_name: divisionName.trim(),
        tagline: tagline.trim() || undefined,
        focus_areas: focusAreas,
        target_market: targetMarket,
      });
      setBrandUrl((result as { brand_url: string }).brand_url);
      setStep('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setStep('market');
    }
  }

  const progressSteps: Step[] = ['division_name', 'tagline', 'focus', 'market'];
  const currentProgressIdx = progressSteps.indexOf(step as Step);

  if (step === 'submitting') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Building your AI division brand…</h2>
          <p className="text-gray-500 text-sm">Setting up your brand and website in The One Brand. This takes just a moment.</p>
        </div>
      </div>
    );
  }

  if (step === 'done') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Your AI division is ready</h2>
          <p className="text-gray-500 text-sm mb-8">
            <strong>{divisionName}</strong> has been created in The One Brand with a draft website using the AI Advisory template. Head over to Brand to customize your logo, colors, and website copy.
          </p>
          <div className="flex flex-col gap-3">
            <a
              href={brandUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors text-center"
            >
              Open in The One Brand →
            </a>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full text-gray-500 hover:text-gray-700 py-2 text-sm transition-colors"
            >
              Go to Studio Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-lg w-full">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex gap-1.5">
            {progressSteps.map((s, i) => (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i <= currentProgressIdx ? 'bg-indigo-600' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-2 text-right">
            {Math.max(currentProgressIdx + 1, 1)} of {progressSteps.length}
          </p>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          {step === 'division_name' && (
            <>
              <div className="mb-6">
                <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">Your AI Division</span>
                <h2 className="text-xl font-semibold text-gray-900 mt-1">What do you want to call your AI division?</h2>
                <p className="text-sm text-gray-500 mt-1">
                  This is the brand name for your AI advisory business. It can be a new name or a sub-brand (e.g., "JPtheGeek AI", "Nexus AI", "Clarity AI").
                </p>
              </div>
              <input
                type="text"
                value={divisionName}
                onChange={e => setDivisionName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && divisionName.trim()) setStep('tagline'); }}
                placeholder="e.g., Acme AI, Clarity AI Services"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent mb-6"
                autoFocus
              />
              <button
                onClick={() => setStep('tagline')}
                disabled={!divisionName.trim()}
                className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-medium py-2.5 rounded-xl text-sm transition-colors"
              >
                Next →
              </button>
            </>
          )}

          {step === 'tagline' && (
            <>
              <div className="mb-6">
                <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">Tagline</span>
                <h2 className="text-xl font-semibold text-gray-900 mt-1">Describe what you do in one line.</h2>
                <p className="text-sm text-gray-500 mt-1">This becomes your AI division's tagline. Keep it short and punchy. You can always update it later.</p>
              </div>
              <input
                type="text"
                value={tagline}
                onChange={e => setTagline(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') setStep('focus'); }}
                placeholder="e.g., We help local businesses win with AI"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent mb-6"
                autoFocus
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setStep('division_name')}
                  className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2"
                >
                  ← Back
                </button>
                <button
                  onClick={() => setStep('focus')}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2.5 rounded-xl text-sm transition-colors"
                >
                  {tagline.trim() ? 'Next →' : 'Skip →'}
                </button>
              </div>
            </>
          )}

          {step === 'focus' && (
            <>
              <div className="mb-6">
                <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">Services</span>
                <h2 className="text-xl font-semibold text-gray-900 mt-1">What AI services will you offer?</h2>
                <p className="text-sm text-gray-500 mt-1">Select all that apply. Your website will be built around these.</p>
              </div>
              <div className="space-y-2 mb-6">
                {FOCUS_OPTIONS.map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => toggleFocus(opt.id)}
                    className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-colors ${
                      focusAreas.includes(opt.id)
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className={`font-medium ${focusAreas.includes(opt.id) ? 'text-indigo-700' : 'text-gray-800'}`}>
                      {opt.label}
                    </span>
                    <span className="block text-xs text-gray-500 mt-0.5">{opt.description}</span>
                  </button>
                ))}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setStep('tagline')}
                  className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2"
                >
                  ← Back
                </button>
                <button
                  onClick={() => setStep('market')}
                  disabled={focusAreas.length === 0}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-medium py-2.5 rounded-xl text-sm transition-colors"
                >
                  Next →
                </button>
              </div>
            </>
          )}

          {step === 'market' && (
            <>
              <div className="mb-6">
                <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">Target Clients</span>
                <h2 className="text-xl font-semibold text-gray-900 mt-1">Who are your ideal AI clients?</h2>
                <p className="text-sm text-gray-500 mt-1">This helps us write the right messaging for your website.</p>
              </div>
              <div className="space-y-2 mb-6">
                {MARKET_OPTIONS.map(opt => (
                  <button
                    key={opt}
                    onClick={() => setTargetMarket(opt)}
                    className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-colors ${
                      targetMarket === opt
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm mb-4">{error}</div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => setStep('focus')}
                  className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2"
                >
                  ← Back
                </button>
                <button
                  onClick={() => void handleSubmit()}
                  disabled={!targetMarket}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-medium py-2.5 rounded-xl text-sm transition-colors"
                >
                  Build My AI Division Brand →
                </button>
              </div>
            </>
          )}
        </div>

        <div className="mt-4 text-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            Skip for now — set up later from your dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
